import { Box, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import { ExpandMore, Lock, Logout, Person } from "@mui/icons-material";
import { useAuth } from "@sal/authentication.react";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { EndSessionRequest } from "@azure/msal-browser";

type Auth = ReturnType<typeof useAuth>;
type ProvidePermissionResult = (auth: Auth) => boolean;

type AuthenticatedUserMenuPermissions = {
	changePassword?: ProvidePermissionResult;
};

export interface AuthenticatedUserMenuProps {
	permissions?: AuthenticatedUserMenuPermissions;
}

const getUsername = (auth: Auth) => {
	const firstName = auth.user?.firstName ?? "";
	const lastName = auth.user?.lastName ?? "";
	return `${firstName} ${lastName}`;
};

export function AuthenticatedUserMenu({ permissions }: AuthenticatedUserMenuProps) {
	const auth = useAuth();
	const navigate = useNavigate();
    const { instance } = useMsal();
	const anchorRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
    

	const hasChangePasswordPermission = permissions?.changePassword ?? (() => false);

	const handleClose = useCallback(() => setOpen(false), []);

	const handleLogout = () => {
		if (auth.authenticationMethod === "MicrosoftSSO") {
			const logoutRequest: EndSessionRequest = {
				postLogoutRedirectUri: "/#/Logout"
			};

			instance.logoutRedirect(logoutRequest);
		} else {
			navigate({ pathname: "/Logout" });
		}
	};

	return (
		<>
			<Box
				ref={anchorRef}
				display="flex"
				alignItems="center"
				gap={2}
				sx={{ cursor: "pointer" }}
				onClick={() => setOpen((x) => !x)}
			>
				<Person color="inherit" />
				<Box display="flex" alignItems="center">
					<Typography
						display="inline-flex"
						fontWeight={600}
						paddingTop={0.5}
						lineHeight={1}
						sx={{ color: "inherit", margin: 0 }}
					>
						{getUsername(auth)}
					</Typography>
					<ExpandMore color="inherit" />
				</Box>
			</Box>
			<Menu
				anchorEl={anchorRef.current}
				open={open}
				onClose={handleClose}
				onClick={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				transformOrigin={{ vertical: "top", horizontal: "center" }}
			>
				{hasChangePasswordPermission(auth) && (
					<MenuItem onClick={() => navigate({ pathname: "/ChangePassword" })}>
						<ListItemIcon>
							<Lock fontSize="small" color="inherit" />
						</ListItemIcon>
						<span>Change Password</span>
					</MenuItem>
				)}
				<MenuItem onClick={handleLogout}>
					<ListItemIcon>
						<Logout fontSize="small" color="inherit" />
					</ListItemIcon>
					<span>Logout</span>
				</MenuItem>
			</Menu>
		</>
	);
}
